import { AI_SERVICES_ORIGIN, MOSIDA_API_BASE_URL, MOSIDA_API_ORIGIN } from "../config/apiUrls";
import { fetchImage } from "./imageReadService";
import { resolvePublicImageUrl, toCdnMediaUrl } from "./fileUploadService";
import { fetchWithTimeout } from "../utils/httpClient";
import { appendOptionalUserLineToPrompt } from "./stylePromptResolver";
import {
  persistHobbyCharacterNologoPairs,
  resolveHobbyCharacterUrlsForComposite,
} from "../utils/hobbyGenerationNavigation";

/** localStorage flag: favourite art is a true transparent PNG (alpha channel). */
export const FAVOURITE_IMG_TRANSPARENT_KEY = "favouriteImgTransparent";

export function isTransparentHobbyBackgroundName(name?: string | null): boolean {
  const n = String(name || "").trim().toLowerCase();
  return (
    n === "transparent background" ||
    n === "no background" ||
    n === "transparent"
  );
}

export function persistFavouriteTransparencyFlag(isTransparent: boolean): void {
  try {
    if (isTransparent) {
      localStorage.setItem(FAVOURITE_IMG_TRANSPARENT_KEY, "1");
    } else {
      localStorage.removeItem(FAVOURITE_IMG_TRANSPARENT_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function readFavouriteTransparencyFlag(): boolean {
  try {
    return localStorage.getItem(FAVOURITE_IMG_TRANSPARENT_KEY) === "1";
  } catch {
    return false;
  }
}

const apiBase = MOSIDA_API_BASE_URL;

export interface CaricatureHobbyCategory {
  id: string;
  name: string;
  description: string | null;
  status: boolean;
  icon: string | null;
  icon_url: string | null;
  items_count?: number;
  created_at: string;
  updated_at: string;
}

export function categoryImageUrl(cat: Pick<CaricatureHobbyCategory, "icon" | "icon_url">): string {
  return cat.icon_url || cat.icon || "";
}

export interface CaricatureHobby {
  id: string;
  category_id: string;
  name: string;
  icon: string | null;
  icon_url: string | null;
  status: boolean;
  created_at: string;
  updated_at: string;
}

export interface IncrementCaricatureHobbyUsageResult {
  hobby_id: string;
  category_id: string;
  hobby_used_count: number;
  category_used_count: number;
}

function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    accept: "application/json",
  };
  const key = process.env.REACT_APP_FAST_API_SECRET_KEY;
  if (key) {
    (headers as Record<string, string>)["X-API-Key"] = key;
  }
  return headers;
}

export async function getActiveCaricatureHobbyCategories(): Promise<{
  success: boolean;
  data: CaricatureHobbyCategory[];
  error?: string;
}> {
  try {
    const response = await fetch(`${apiBase}/caricature-hobby-categories/active`, {
      method: "GET",
      headers: getHeaders(),
    });
    if (!response.ok) {
      const text = await response.text();
      return {
        success: false,
        data: [],
        error: `Failed to load categories: ${response.status} ${text}`,
      };
    }
    const json = await response.json();
    const data = Array.isArray(json?.data) ? json.data : [];
    return { success: Boolean(json?.success !== false), data, error: json?.message };
  } catch (e: any) {
    return {
      success: false,
      data: [],
      error: e?.message || "Network error while loading categories",
    };
  }
}

export async function getCaricatureHobbiesByCategory(
  categoryId: string
): Promise<{ success: boolean; data: CaricatureHobby[]; error?: string }> {
  if (!categoryId) {
    return { success: false, data: [], error: "Missing category" };
  }
  try {
    const params = new URLSearchParams({ category_id: categoryId });
    const response = await fetch(`${apiBase}/caricature-hobbies?${params}`, {
      method: "GET",
      headers: getHeaders(),
    });
    if (!response.ok) {
      const text = await response.text();
      return {
        success: false,
        data: [],
        error: `Failed to load hobbies: ${response.status} ${text}`,
      };
    }
    const json = await response.json();
    const data = Array.isArray(json?.data) ? json.data : [];
    return { success: Boolean(json?.success !== false), data, error: json?.message };
  } catch (e: any) {
    return {
      success: false,
      data: [],
      error: e?.message || "Network error while loading hobbies",
    };
  }
}

export async function getPopularCaricatureHobbies(): Promise<{
  success: boolean;
  data: CaricatureHobby[];
  error?: string;
}> {
  try {
    const response = await fetch(`${apiBase}/caricature-hobbies/popular`, {
      method: "GET",
      headers: getHeaders(),
    });
    if (!response.ok) {
      const text = await response.text();
      return {
        success: false,
        data: [],
        error: `Failed to load popular hobbies: ${response.status} ${text}`,
      };
    }
    const json = await response.json();
    const data = Array.isArray(json?.data)
      ? json.data
      : Array.isArray(json?.hobbies)
        ? json.hobbies
        : Array.isArray(json)
          ? json
          : [];
    return { success: Boolean(json?.success !== false), data, error: json?.message };
  } catch (e: any) {
    return {
      success: false,
      data: [],
      error: e?.message || "Network error while loading popular hobbies",
    };
  }
}

function parseSearchResponse(json: any): {
  hobbies: CaricatureHobby[];
  categoryCounts: Record<string, number>;
} {
  const raw = Array.isArray(json?.data)
    ? json.data
    : Array.isArray(json?.hobbies)
      ? json.hobbies
      : Array.isArray(json)
        ? json
        : [];

  const hobbies: CaricatureHobby[] = [];
  const categoryCounts: Record<string, number> = {};

  raw.forEach((item: any) => {
    const items = Array.isArray(item?.items) ? item.items : [];
    const count =
      typeof item?.items_count === "number" ? item.items_count : items.length;
    if (item?.id) {
      categoryCounts[String(item.id)] = count;
    }
    items.forEach((hobby: any) => {
      if (hobby) hobbies.push(hobby);
    });
  });

  return { hobbies, categoryCounts };
}

export async function searchCaricatureHobbies(
  query: string
): Promise<{
  success: boolean;
  data: CaricatureHobby[];
  categoryCounts: Record<string, number>;
  error?: string;
}> {
  const term = query.trim();
  if (!term) {
    return { success: false, data: [], categoryCounts: {}, error: "Missing search term" };
  }

  try {
    const params = new URLSearchParams({ search: term });
    const response = await fetch(`${apiBase}/caricature-hobbies/search?${params}`, {
      method: "GET",
      headers: getHeaders(),
    });
    if (!response.ok) {
      const text = await response.text();
      return {
        success: false,
        data: [],
        categoryCounts: {},
        error: `Failed to search hobbies: ${response.status} ${text}`,
      };
    }
    const json = await response.json();
    const parsed = parseSearchResponse(json);
    return {
      success: Boolean(json?.success !== false),
      data: parsed.hobbies,
      categoryCounts: parsed.categoryCounts,
      error: json?.message,
    };
  } catch (e: any) {
    return {
      success: false,
      data: [],
      categoryCounts: {},
      error: e?.message || "Network error while searching hobbies",
    };
  }
}

export async function incrementCaricatureHobbyUsage(
  artId: string,
  categoryId: string
): Promise<{
  success: boolean;
  data?: IncrementCaricatureHobbyUsageResult;
  error?: string;
}> {
  if (!artId || !categoryId) {
    return { success: false, error: "Missing art_id or category_id" };
  }
  try {
    const response = await fetch(`${apiBase}/caricature-hobbies/usage/increment`, {
      method: "POST",
      headers: {
        ...getHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        art_id: artId,
        category_id: categoryId,
      }),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        error: json?.message || `Failed to increment usage: ${response.status}`,
      };
    }
    return {
      success: Boolean(json?.success !== false),
      data: json?.data,
      error: json?.message,
    };
  } catch (e: any) {
    return {
      success: false,
      error: e?.message || "Network error while incrementing usage",
    };
  }
}

export function hobbyImageUrl(hobby: CaricatureHobby): string {
  return hobby.icon_url || hobby.icon || "";
}

export interface CaricatureHobbyBackgroundImage {
  id: string;
  name: string;
  image: string | null;
  image_url: string | null;
  status: boolean;
  created_at: string;
  updated_at: string;
}

export function backgroundImageUrl(bg: CaricatureHobbyBackgroundImage): string {
  const raw = (bg.image_url || bg.image || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  if (raw.startsWith("/")) return `${MOSIDA_API_ORIGIN}${raw}`;
  return `${MOSIDA_API_ORIGIN}/${raw}`;
}

export interface GenerateHobbyCharactersBatchParams {
  hobbyNames: string[];
  selfieSources: string[];
  /** Optional creative instruction appended to each character_prompt (same as image-transformation). */
  customLine?: string;
}

export interface GenerateHobbyCharactersBatchResult {
  success: boolean;
  data?: any;
  imageUrls: string[];
  /** Same order as `imageUrls`, without Mosida logo when the batch API provides it. */
  imageUrlsNoLogo: string[];
  error?: string;
}

export interface CompositeCharactersOnBackgroundParams {
  characterUrls: string[];
  backgroundUrl?: string;
  /** Used to pick compositing prompt (e.g. "Transparent Background"). */
  backgroundName?: string;
  /** Explicit override; when true, skips logo and produces a true transparent PNG. */
  isTransparent?: boolean;
  position?: string;
  scale?: number;
  canvasSize?: string;
  dpi?: number;
  useGeminiCompositing?: boolean;
  compositingPrompt?: string;
}

export interface CompositeCharactersOnBackgroundResult {
  success: boolean;
  imageUrl?: string;
  data?: any;
  error?: string;
}

const hobbyBatchEndpoint = `${AI_SERVICES_ORIGIN}/generate-characters-batch-web`;
const compositeCharactersOnBackgroundEndpoint = `${AI_SERVICES_ORIGIN}/composite-characters-on-background`;
const extractPersonsFromGroupEndpoint = `${AI_SERVICES_ORIGIN.replace(/\/+$/, "")}/extract-persons-from-group`;

export interface ExtractPersonsFromGroupResult {
  success: boolean;
  imageUrls: string[];
  results?: any[];
  data?: any;
  error?: string;
  message?: string;
}

function absolutizeAiServiceUrl(url: string): string {
  const trimmed = (url || "").trim();
  if (!trimmed) return "";
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  const origin = AI_SERVICES_ORIGIN.replace(/\/+$/, "");
  if (!origin) return trimmed;
  return trimmed.startsWith("/") ? `${origin}${trimmed}` : `${origin}/${trimmed}`;
}

/** Same template as hobby batch generation; used for AI selfie arts with `is_caricature_hobby`. */
export function buildCaricatureHobbyCharacterPrompt(hobbyName: string): string {
  const normalized = (hobbyName || "a hobby").trim();
  return `A full-body, hand-drawn cartoon-style caricature with slightly exaggerated features and proportionally larger heads. Each character is actively doing ${normalized}, wearing a detailed and activity-appropriate outfit in bright and vibrant colors. They are engaged in grounded, realistic action poses with clear contact to the ground to avoid any floating appearance. Use bold black outlines, vivid comic-style coloring, and a humorous, energetic aesthetic inspired by traditional marker and watercolor caricatures. The background must be plain white with no patterns or scenery. Maintain full visibility of each subject from head to toe; do not crop, remove, or merge any person. Keep the exaggeration subtle and flattering, not overly distorted.`;
}

function buildHobbyPrompt(hobbyName: string): string {
  return buildCaricatureHobbyCharacterPrompt(hobbyName);
}

async function sourceToFile(source: string, index: number): Promise<File> {
  const response = await fetchImage(source);
  if (!response.ok) {
    throw new Error(`Image fetch failed for selfie_${index}: ${response.status}`);
  }
  const blob = await response.blob();
  const extFromType = blob.type?.split("/")[1] || "png";
  let filename = `selfie_${index}.${extFromType}`;
  try {
    const srcUrl = new URL(source);
    const last = srcUrl.pathname.split("/").filter(Boolean).pop();
    if (last) filename = decodeURIComponent(last);
  } catch {
    /* source can be data URL */
  }
  return new File([blob], filename, { type: blob.type || "image/png" });
}

function collectImageUrls(payload: any): string[] {
  const found = new Set<string>();
  const visit = (value: any) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (typeof value === "object") {
      // Prefer logo URL only — never treat image_url_nologo as the primary result.
      const direct =
        value.image_url ||
        value.imageUrl ||
        value.output_url ||
        value.outputUrl ||
        value.result_url ||
        value.resultUrl ||
        value.url;
      if (typeof direct === "string" && /^https?:\/\//i.test(direct)) {
        found.add(direct);
      }
      // Skip bare string values (e.g. image_url_nologo) so they are not collected as primaries.
      Object.entries(value).forEach(([key, child]) => {
        if (/nologo/i.test(key)) return;
        visit(child);
      });
    }
  };
  visit(payload);
  return Array.from(found);
}

/** Extract logo + no-logo character URLs from generate-characters-batch-web results. */
function collectBatchCharacterImageUrls(payload: any): {
  imageUrls: string[];
  imageUrlsNoLogo: string[];
} {
  const results = Array.isArray(payload?.results) ? payload.results : [];
  const imageUrls: string[] = [];
  const imageUrlsNoLogo: string[] = [];

  for (const item of results) {
    if (!item || item.success === false) continue;
    const withLogoRaw =
      (typeof item.image_url === "string" && item.image_url) ||
      (typeof item.imageUrl === "string" && item.imageUrl) ||
      "";
    const withLogo = absolutizeAiServiceUrl(String(withLogoRaw).trim());
    if (!withLogo || !/^https?:\/\//i.test(withLogo)) continue;

    const noLogoRaw =
      (typeof item.image_url_nologo === "string" && item.image_url_nologo) ||
      (typeof item.imageUrlNoLogo === "string" && item.imageUrlNoLogo) ||
      "";
    const noLogo = absolutizeAiServiceUrl(String(noLogoRaw).trim());

    imageUrls.push(withLogo);
    imageUrlsNoLogo.push(noLogo && /^https?:\/\//i.test(noLogo) ? noLogo : withLogo);
  }

  if (imageUrls.length > 0) {
    return { imageUrls, imageUrlsNoLogo };
  }

  const fallback = collectImageUrls(payload).map(absolutizeAiServiceUrl).filter(Boolean);
  return { imageUrls: fallback, imageUrlsNoLogo: [...fallback] };
}

const defaultCompositingPrompt = `Use the attached background exactly as it is - same colors, shapes, and watercolor effect.

Do not change or redraw the background.

CRITICAL CHARACTER PRESERVATION:

- Merge all attached character images into this background, keeping each person EXACTLY as they appear in their original image.
- Each character must maintain their EXACT face, pose, outfit, colors, and equipment from their original image.
- Do NOT add any extra objects, equipment, or elements to any character.
- Each character should only have the equipment/objects they had in their original image.
- Do NOT mix equipment between characters.
- Do NOT add any new sports equipment, balls, or objects that were not in the original character images.

CHARACTER PLACEMENT:

- Place the characters naturally around the large "LOVE" text in the background.
- Make the parents slightly larger than the children for natural proportions.
- Position characters so they don't overlap inappropriately.

STRICT RULES:

- Keep the background identical every time.
- Do not add, remove, or alter any person.
- Do NOT add any extra objects, equipment, or elements.
- Each character keeps ONLY what they had in their original image.
- Only adjust positioning and size slightly to blend them naturally.`;

const noBackgroundCompositingPrompt = `Merge all attached character images onto a clean plain white background with no scenery, patterns, or extra objects.

CRITICAL CHARACTER PRESERVATION:

- Keep each person EXACTLY as they appear in their original image.
- Each character must maintain their EXACT face, pose, outfit, colors, and equipment from their original image.
- Do NOT add any extra objects, equipment, or elements to any character.
- Do NOT mix equipment between characters.

CHARACTER PLACEMENT:

- Place the characters naturally together so they do not overlap inappropriately.
- Keep full visibility of each subject from head to toe.

STRICT RULES:

- Do not add, remove, or alter any person.
- Do NOT add any extra objects, equipment, or elements.
- Only adjust positioning and size slightly to blend them naturally.`;

/** Merge characters only — used before remove-bg when Transparent Background is selected. */
const transparentMergeCompositingPrompt = `Merge all attached character images onto a clean plain white background with no scenery, patterns, or extra objects.

CRITICAL CHARACTER PRESERVATION:

- Keep each person EXACTLY as they appear in their original image.
- Each character must maintain their EXACT face, pose, outfit, colors, and equipment from their original image.
- Do NOT add any extra objects, equipment, or elements to any character.
- Do NOT mix equipment between characters.

CHARACTER PLACEMENT:

- Place the characters naturally together so they do not overlap inappropriately.
- Keep full visibility of each subject from head to toe.

STRICT RULES:

- Do not add, remove, or alter any person.
- Do NOT add any extra objects, equipment, or elements.
- Only adjust positioning and size slightly to blend them naturally.
- Use a solid plain white background only (no checkerboard).`;

/**
 * Clear edge-connected white/near-white matte on a canvas ImageData in place.
 * Keeps colored content (grass, clothes, outlines). Returns cleared pixel count.
 */
function clearWhiteMatteFromImageData(imageData: ImageData): number {
  const { data, width, height } = imageData;
  const total = width * height;
  const isMatte = new Uint8Array(total);

  const isTransparencyMattePixel = (index: number) => {
    const i = index * 4;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    if (a < 8) return true;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const chroma = max - min;
    // Keep any saturated color (grass, jersey, skin, outlines).
    if (chroma > 18) return false;
    // Near-white studio / plain background
    if (min >= 235) return true;
    // Soft anti-aliased white fringe next to the subject
    if (min >= 220 && chroma <= 12) return true;
    return false;
  };

  for (let p = 0; p < total; p += 1) {
    if (isTransparencyMattePixel(p)) isMatte[p] = 1;
  }

  const visited = new Uint8Array(total);
  const queue = new Int32Array(total);
  let qStart = 0;
  let qEnd = 0;

  const enqueueEdge = (x: number, y: number) => {
    const p = y * width + x;
    if (!isMatte[p] || visited[p]) return;
    visited[p] = 1;
    queue[qEnd++] = p;
  };

  for (let x = 0; x < width; x += 1) {
    enqueueEdge(x, 0);
    enqueueEdge(x, height - 1);
  }
  for (let y = 0; y < height; y += 1) {
    enqueueEdge(0, y);
    enqueueEdge(width - 1, y);
  }

  while (qStart < qEnd) {
    const p = queue[qStart++];
    const x = p % width;
    const y = (p - x) / width;
    const neighbors = [p - 1, p + 1, p - width, p + width];
    for (let n = 0; n < neighbors.length; n += 1) {
      const np = neighbors[n];
      if (np < 0 || np >= total) continue;
      const nx = np % width;
      const ny = (np - nx) / width;
      if (Math.abs(nx - x) + Math.abs(ny - y) !== 1) continue;
      if (!isMatte[np] || visited[np]) continue;
      visited[np] = 1;
      queue[qEnd++] = np;
    }
  }

  let cleared = 0;
  for (let p = 0; p < total; p += 1) {
    if (!visited[p]) continue;
    data[p * 4 + 3] = 0;
    cleared += 1;
  }

  // Clear a 1px near-white fringe around the cut edge (halo cleanup).
  if (cleared > 0) {
    const fringe = new Uint8Array(total);
    for (let p = 0; p < total; p += 1) {
      if (!visited[p]) continue;
      const x = p % width;
      const y = (p - x) / width;
      const neighbors = [p - 1, p + 1, p - width, p + width];
      for (let n = 0; n < neighbors.length; n += 1) {
        const np = neighbors[n];
        if (np < 0 || np >= total || visited[np] || fringe[np]) continue;
        const nx = np % width;
        const ny = (np - nx) / width;
        if (Math.abs(nx - x) + Math.abs(ny - y) !== 1) continue;
        if (!isTransparencyMattePixel(np)) continue;
        fringe[np] = 1;
      }
    }
    for (let p = 0; p < total; p += 1) {
      if (!fringe[p]) continue;
      data[p * 4 + 3] = 0;
      cleared += 1;
    }
  }

  // If almost nothing was cleared, fall back to clearing all matte-colored pixels.
  if (cleared < total * 0.02) {
    cleared = 0;
    for (let p = 0; p < total; p += 1) {
      if (!isMatte[p]) continue;
      data[p * 4 + 3] = 0;
      cleared += 1;
    }
  }

  return cleared;
}

/**
 * Strip white studio background from an already-decoded image for product mockups.
 * Returns a canvas with true alpha, or null if nothing useful could be done.
 */
export function stripWhiteMatteFromDrawable(
  source: CanvasImageSource,
  sourceWidth?: number,
  sourceHeight?: number
): HTMLCanvasElement | null {
  try {
    const width =
      sourceWidth ||
      (source as HTMLImageElement).naturalWidth ||
      (source as HTMLCanvasElement).width ||
      0;
    const height =
      sourceHeight ||
      (source as HTMLImageElement).naturalHeight ||
      (source as HTMLCanvasElement).height ||
      0;
    if (!width || !height) return null;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return null;

    ctx.drawImage(source, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    const cleared = clearWhiteMatteFromImageData(imageData);
    if (cleared <= 0) return null;
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  } catch {
    return null;
  }
}

/** True when corner samples already have meaningful alpha (transparent PNG). */
export function drawableHasTransparency(
  source: CanvasImageSource,
  sourceWidth?: number,
  sourceHeight?: number
): boolean {
  try {
    const width =
      sourceWidth ||
      (source as HTMLImageElement).naturalWidth ||
      (source as HTMLCanvasElement).width ||
      0;
    const height =
      sourceHeight ||
      (source as HTMLImageElement).naturalHeight ||
      (source as HTMLCanvasElement).height ||
      0;
    if (!width || !height) return false;

    const canvas = document.createElement("canvas");
    canvas.width = Math.min(width, 64);
    canvas.height = Math.min(height, 64);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return false;
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparent = 0;
    for (let i = 3; i < data.length; i += 16) {
      if (data[i] < 16) transparent += 1;
    }
    return transparent > 8;
  } catch {
    return false;
  }
}

/**
 * Convert edge-connected white/near-white matte pixels to true PNG alpha.
 * Keeps colored content (e.g. grass, clothes) — does NOT use AI remove-bg.
 */
export async function convertBakedTransparencyMatteToPng(
  sourceUrl: string
): Promise<string | null> {
  try {
    let blob: Blob | null = null;
    try {
      const direct = await fetch(sourceUrl, { mode: "cors", credentials: "omit" });
      if (direct.ok) blob = await direct.blob();
    } catch {
      /* fall through to proxy */
    }
    if (!blob) {
      const response = await fetchImage(sourceUrl);
      if (!response.ok) return null;
      blob = await response.blob();
    }

    const bitmap = await createImageBitmap(blob);
    const canvas = stripWhiteMatteFromDrawable(bitmap, bitmap.width, bitmap.height);
    bitmap.close?.();
    if (!canvas) return null;
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

export async function generateHobbyCharactersBatch(
  params: GenerateHobbyCharactersBatchParams
): Promise<GenerateHobbyCharactersBatchResult> {
  const hobbyNames = params.hobbyNames || [];
  const selfieSources = params.selfieSources || [];
  if (!hobbyNames.length || !selfieSources.length) {
    return {
      success: false,
      imageUrls: [],
      imageUrlsNoLogo: [],
      error: "Missing hobbies or selfies for batch generation.",
    };
  }

  try {
    const count = Math.min(hobbyNames.length, selfieSources.length);
    const customLine = (params.customLine || "").trim();
    const items = hobbyNames.slice(0, count).map((name) => ({
      character_prompt: appendOptionalUserLineToPrompt(buildHobbyPrompt(name), customLine),
      station: "caricature",
      dpi: 300,
      canvas_size: "3:2",
      ...(customLine ? { custom_text: customLine } : {}),
    }));

    const form = new FormData();
    form.append("items", JSON.stringify(items));

    const files = await Promise.all(
      selfieSources.slice(0, count).map((src, index) => sourceToFile(src, index))
    );
    files.forEach((file, index) => {
      form.append(`selfie_${index}`, file, file.name);
    });

    const response = await fetchWithTimeout(hobbyBatchEndpoint, {
      method: "POST",
      headers: {
        accept: "*/*",
      },
      body: form,
      timeoutMs: 120000,
    });

    const rawText = await response.text();
    let json: any = null;
    try {
      json = rawText ? JSON.parse(rawText) : {};
    } catch {
      json = { raw: rawText };
    }

    if (!response.ok) {
      return {
        success: false,
        imageUrls: [],
        imageUrlsNoLogo: [],
        data: json,
        error: `Batch API failed: ${response.status} ${response.statusText}`,
      };
    }

    const { imageUrls, imageUrlsNoLogo } = collectBatchCharacterImageUrls(json);
    persistHobbyCharacterNologoPairs(imageUrls, imageUrlsNoLogo);
    return {
      success: imageUrls.length > 0 || json?.success !== false,
      imageUrls,
      imageUrlsNoLogo,
      data: json,
      error: imageUrls.length ? undefined : "API responded, but no image URLs were found.",
    };
  } catch (e: any) {
    return {
      success: false,
      imageUrls: [],
      imageUrlsNoLogo: [],
      error: e?.message || "Network error while generating hobby caricatures.",
    };
  }
}

/**
 * Produce a true alpha-channel PNG for Transparent Background.
 * Only clears edge-connected white matte so grass / props stay intact.
 * Avoids AI remove-bg which often deletes grass and small foreground details.
 */
async function ensureTrueTransparentPng(sourceUrl: string): Promise<string> {
  const transparentDataUrl = await convertBakedTransparencyMatteToPng(sourceUrl);
  if (!transparentDataUrl) return sourceUrl;

  // Prefer a public CDN URL, but keep the local PNG if upload flattens alpha to white.
  const uploaded = await resolvePublicImageUrl(transparentDataUrl);
  if (!uploaded.success || !uploaded.url) return transparentDataUrl;

  const cdnUrl = toCdnMediaUrl(uploaded.url) || uploaded.url;
  try {
    const probe = await fetch(cdnUrl, { mode: "cors", credentials: "omit" });
    if (probe.ok) {
      const blob = await probe.blob();
      // JPEG cannot carry alpha — do not use a flattened upload for transparent art.
      if (/jpeg|jpg/i.test(blob.type)) return transparentDataUrl;
      const bitmap = await createImageBitmap(blob);
      const hasAlpha = drawableHasTransparency(bitmap, bitmap.width, bitmap.height);
      bitmap.close?.();
      if (hasAlpha) return cdnUrl;
      return transparentDataUrl;
    }
  } catch {
    /* CDN probe failed — still try CDN URL for remote consumers */
  }
  return cdnUrl;
}

export async function compositeCharactersOnBackground(
  params: CompositeCharactersOnBackgroundParams
): Promise<CompositeCharactersOnBackgroundResult> {
  const rawCharacterUrls = (params.characterUrls || []).filter(
    (url) => typeof url === "string" && url.trim().length > 0
  );
  const backgroundUrl = (params.backgroundUrl || "").trim();
  const backgroundName = String(params.backgroundName || "").trim().toLowerCase();
  const isTransparentBackground =
    params.isTransparent === true || isTransparentHobbyBackgroundName(backgroundName);
  // Transparent: use image_url_nologo from batch generation so the logo is not composited in.
  const characterUrls = resolveHobbyCharacterUrlsForComposite(
    rawCharacterUrls,
    isTransparentBackground
  );
  if (!characterUrls.length) {
    return {
      success: false,
      error: "Missing character URLs for compositing.",
    };
  }

  try {
    // Transparent Background: never send the admin sample as background_url (often white /
    // checkerboard preview). Always hit composite so backend can skip Mosida logo via is_transparent.
    if (isTransparentBackground) {
      const form = new FormData();
      form.append("character_urls", JSON.stringify(characterUrls));
      form.append("position", params.position || "center");
      form.append("scale", String(params.scale ?? 1));
      form.append("canvas_size", params.canvasSize ?? "3:2");
      form.append("dpi", String(params.dpi ?? 300));
      form.append("use_gemini_compositing", String(params.useGeminiCompositing ?? true));
      form.append("is_transparent", "true");
      form.append(
        "compositing_prompt",
        params.compositingPrompt || transparentMergeCompositingPrompt
      );

      // 300s matches nginx; the previous 120s abort showed as "canceled" in DevTools
      // while Gemini was still working (esp. 4 characters on a print-size canvas).
      const response = await fetchWithTimeout(compositeCharactersOnBackgroundEndpoint, {
        method: "POST",
        headers: {
          accept: "*/*",
        },
        body: form,
        timeoutMs: 300000,
      });

      const rawText = await response.text();
      let json: any = null;
      try {
        json = rawText ? JSON.parse(rawText) : {};
      } catch {
        json = { raw: rawText };
      }

      if (!response.ok) {
        return {
          success: false,
          data: json,
          error: `Composite API failed: ${response.status} ${response.statusText}`,
        };
      }

      const imageUrls = collectImageUrls(json);
      let imageUrl = imageUrls[0];
      if (!imageUrl) {
        return {
          success: false,
          data: json,
          error: "API responded, but no composited image URL was found.",
        };
      }

      imageUrl = await ensureTrueTransparentPng(imageUrl);

      return {
        success: Boolean(imageUrl),
        imageUrl,
        data: json,
        error: imageUrl ? undefined : "Failed to create transparent PNG.",
      };
    }

    const form = new FormData();
    form.append("character_urls", JSON.stringify(characterUrls));
    if (backgroundUrl) {
      form.append("background_url", backgroundUrl);
    }
    form.append("position", params.position || "center");
    form.append("scale", String(params.scale ?? 1));
    form.append("canvas_size", params.canvasSize ?? "3:2");
    form.append("dpi", String(params.dpi ?? 300));
    form.append("use_gemini_compositing", String(params.useGeminiCompositing ?? true));
    // true = skip Mosida logo (transparent); false = add logo on non-transparent art.
    form.append("is_transparent", isTransparentBackground ? "true" : "false");
    const resolvedCompositingPrompt =
      params.compositingPrompt ||
      (backgroundUrl ? defaultCompositingPrompt : noBackgroundCompositingPrompt);
    form.append("compositing_prompt", resolvedCompositingPrompt);

    // 300s matches nginx; the previous 120s abort showed as "canceled" in DevTools
    // while Gemini was still working (esp. 4 characters on a print-size canvas).
    const response = await fetchWithTimeout(compositeCharactersOnBackgroundEndpoint, {
      method: "POST",
      headers: {
        accept: "*/*",
      },
      body: form,
      timeoutMs: 300000,
    });

    const rawText = await response.text();
    let json: any = null;
    try {
      json = rawText ? JSON.parse(rawText) : {};
    } catch {
      json = { raw: rawText };
    }

    if (!response.ok) {
      return {
        success: false,
        data: json,
        error: `Composite API failed: ${response.status} ${response.statusText}`,
      };
    }

    const imageUrls = collectImageUrls(json);
    const imageUrl = imageUrls[0];

    return {
      success: Boolean(imageUrl) || json?.success !== false,
      imageUrl,
      data: json,
      error: imageUrl ? undefined : "API responded, but no composited image URL was found.",
    };
  } catch (e: any) {
    return {
      success: false,
      error: e?.message || "Network error while compositing characters on background.",
    };
  }
}

/**
 * Split a group photo into individual person images.
 * Uploads local/camera images to S3 first, then POSTs `group_image_url`.
 */
export async function extractPersonsFromGroup(
  groupImageSource: string,
  options?: { personCount?: number; maxWorkers?: number; maxPersons?: number }
): Promise<ExtractPersonsFromGroupResult> {
  const source = (groupImageSource || "").trim();
  if (!source) {
    return { success: false, imageUrls: [], error: "Missing group photo." };
  }

  try {
    // Upload to S3 when needed, then always pass the public CDN URL to Google AI.
    const uploaded = await resolvePublicImageUrl(source);
    const groupImageCdnUrl = toCdnMediaUrl(uploaded.url || "");
    if (!uploaded.success || !groupImageCdnUrl) {
      return {
        success: false,
        imageUrls: [],
        error: uploaded.error || "Failed to upload group photo to CDN.",
      };
    }

    const form = new FormData();
    form.append("group_image_url", groupImageCdnUrl);

    // Exact count skips the slow Gemini "detect people" pass.
    if (options?.personCount && options.personCount > 0) {
      form.append("person_count", String(options.personCount));
    }
    // if (options?.maxPersons && options.maxPersons > 0) {
    //   form.append("max_persons", String(options.maxPersons));
    // }
    // Parallel person cutouts (server caps this; 1 = sequential/safe).
    if (options?.maxWorkers && options.maxWorkers > 0) {
      form.append("max_workers", String(options.maxWorkers));
    }

    const headers: Record<string, string> = { accept: "application/json" };
    const apiKey = process.env.REACT_APP_FAST_API_SECRET_KEY;
    if (apiKey) headers["X-API-Key"] = apiKey;

    // Exact count + parallel workers usually finishes well under 3 minutes.
    const response = await fetchWithTimeout(extractPersonsFromGroupEndpoint, {
      method: "POST",
      headers,
      body: form,
      timeoutMs: 240000,
    });

    const rawText = await response.text();
    let json: any = null;
    try {
      json = rawText ? JSON.parse(rawText) : {};
    } catch {
      json = { raw: rawText };
    }

    const results = Array.isArray(json?.results) ? json.results : [];
    const imageUrls = results
      .filter((item: any) => item && item.success !== false)
      .map((item: any) =>
        absolutizeAiServiceUrl(
          String(item.image_url || item.imageUrl || item.local_path || item.localPath || "")
        )
      )
      .filter((url: string) => Boolean(url));

    // Fallback: walk payload if results[] shape differs.
    const fallbackUrls = imageUrls.length ? imageUrls : collectImageUrls(json).map(absolutizeAiServiceUrl);

    if (!response.ok || json?.success === false) {
      return {
        success: false,
        imageUrls: fallbackUrls,
        results,
        data: json,
        error: json?.error || json?.message || `Extract API failed: ${response.status} ${response.statusText}`,
        message: json?.message,
      };
    }

    if (!fallbackUrls.length) {
      return {
        success: false,
        imageUrls: [],
        results,
        data: json,
        error: json?.error || json?.message || "No people were found in the group photo.",
        message: json?.message,
      };
    }

    return {
      success: true,
      imageUrls: fallbackUrls,
      results,
      data: json,
      message: json?.message,
    };
  } catch (e: any) {
    return {
      success: false,
      imageUrls: [],
      error: e?.message || "Network error while extracting people from the group photo.",
    };
  }
}

export async function getActiveCaricatureHobbyBackgroundImages(): Promise<{
  success: boolean;
  data: CaricatureHobbyBackgroundImage[];
  error?: string;
}> {
  try {
    const response = await fetch(`${apiBase}/caricature-hobby-background-images/active`, {
      method: "GET",
      headers: getHeaders(),
    });
    if (!response.ok) {
      const text = await response.text();
      return {
        success: false,
        data: [],
        error: `Failed to load backgrounds: ${response.status} ${text}`,
      };
    }
    const json = await response.json();
    const rawList = Array.isArray(json?.data)
      ? json.data
      : Array.isArray(json)
        ? json
        : Array.isArray(json?.data?.items)
          ? json.data.items
          : Array.isArray(json?.items)
            ? json.items
            : [];
    const data = (rawList as CaricatureHobbyBackgroundImage[]).filter((bg) => {
      const isTransparentBackground = isTransparentHobbyBackgroundName(bg?.name);
      // Keep Transparent Background even when it has no image_url (UI shows checkerboard tile).
      return isTransparentBackground || Boolean(backgroundImageUrl(bg));
    });
    return { success: Boolean(json?.success !== false), data, error: json?.message };
  } catch (e: any) {
    return {
      success: false,
      data: [],
      error: e?.message || "Network error while loading backgrounds",
    };
  }
}
